import { PaletteMode } from '@mui/material';

export type StatusKind = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'purple' | 'pink' | 'teal';

export type ChipSurface = { bg: string; fg: string; border: string };

/** Mode-aware pastel status surfaces that remain readable in dark mode. */
export function getStatusSurface(kind: StatusKind, mode: PaletteMode): ChipSurface {
  const dark = mode === 'dark';
  switch (kind) {
    case 'success':
      return dark
        ? { bg: 'rgba(46, 125, 50, 0.22)', fg: '#81c784', border: 'rgba(129, 199, 132, 0.4)' }
        : { bg: '#e8f5e9', fg: '#2e7d32', border: '#c8e6c9' };
    case 'warning':
      return dark
        ? { bg: 'rgba(245, 124, 0, 0.22)', fg: '#ffb74d', border: 'rgba(255, 183, 77, 0.4)' }
        : { bg: '#fff3e0', fg: '#e65100', border: '#ffe0b2' };
    case 'error':
      return dark
        ? { bg: 'rgba(211, 47, 47, 0.22)', fg: '#ef9a9a', border: 'rgba(239, 154, 154, 0.4)' }
        : { bg: '#ffebee', fg: '#c62828', border: '#ffcdd2' };
    case 'info':
      return dark
        ? { bg: 'rgba(25, 118, 210, 0.22)', fg: '#90caf9', border: 'rgba(144, 202, 249, 0.4)' }
        : { bg: '#e3f2fd', fg: '#1565c0', border: '#bbdefb' };
    case 'purple':
      return dark
        ? { bg: 'rgba(156, 39, 176, 0.24)', fg: '#ce93d8', border: 'rgba(206, 147, 216, 0.4)' }
        : { bg: '#f3e8ff', fg: '#7c3aed', border: '#e9d5ff' };
    case 'pink':
      return dark
        ? { bg: 'rgba(233, 30, 99, 0.22)', fg: '#f48fb1', border: 'rgba(244, 143, 177, 0.4)' }
        : { bg: '#fce7f3', fg: '#9d174d', border: '#fbcfe8' };
    case 'teal':
      return dark
        ? { bg: 'rgba(0, 151, 167, 0.22)', fg: '#4dd0e1', border: 'rgba(77, 208, 225, 0.4)' }
        : { bg: '#e0f7fa', fg: '#0c4a6e', border: '#b2ebf2' };
    default:
      return dark
        ? { bg: 'rgba(255, 255, 255, 0.08)', fg: '#c4c7cc', border: 'rgba(255, 255, 255, 0.16)' }
        : { bg: '#f5f5f5', fg: '#757575', border: '#e0e0e0' };
  }
}

export function getRiskSurface(level: string, mode: PaletteMode): ChipSurface {
  const key = String(level || '').toLowerCase();
  if (key === 'critical') {
    return mode === 'dark'
      ? { bg: 'rgba(183, 28, 28, 0.28)', fg: '#ef9a9a', border: 'rgba(239, 154, 154, 0.45)' }
      : { bg: '#fce8e8', fg: '#b71c1c', border: '#ffcdd2' };
  }
  if (key === 'high') return getStatusSurface('error', mode);
  if (key === 'medium') return getStatusSurface('warning', mode);
  if (key === 'low') return getStatusSurface('success', mode);
  if (key.includes('review')) return getStatusSurface('info', mode);
  if (key.includes('need') || key.includes('clarif')) return getStatusSurface('warning', mode);
  return getStatusSurface('neutral', mode);
}

export function getEnvironmentSurface(environment: string, mode: PaletteMode): ChipSurface {
  const value = String(environment || '').toLowerCase();
  if (value === 'production' || value === 'prod') return getStatusSurface('success', mode);
  if (value === 'staging' || value === 'uat') return getStatusSurface('warning', mode);
  if (value === 'development' || value === 'dev') return getStatusSurface('info', mode);
  if (value === 'testing' || value === 'test') return getStatusSurface('neutral', mode);
  return getStatusSurface('neutral', mode);
}

export function getProtocolSurface(type: string, mode: PaletteMode): ChipSurface {
  return String(type || '').toUpperCase() === 'OIDC'
    ? getStatusSurface('info', mode)
    : getStatusSurface('purple', mode);
}

const ROLE_META: Record<string, { kind: StatusKind; label: string }> = {
  super_admin: { kind: 'error', label: 'Super Admin' },
  app_admin: { kind: 'info', label: 'App Admin' },
  iga_admin: { kind: 'success', label: 'IGA Admin' },
  sso_admin: { kind: 'purple', label: 'SSO Admin' },
  pam_admin: { kind: 'pink', label: 'PAM Admin' },
  grc_admin: { kind: 'warning', label: 'GRC Admin' },
  auditor: { kind: 'teal', label: 'Auditor' },
  user: { kind: 'neutral', label: 'User' },
  delegate: { kind: 'success', label: 'Delegate' },
};

export function getRoleChipStyle(
  role: string,
  mode: PaletteMode
): ChipSurface & { label: string } {
  const meta = ROLE_META[role] || {
    kind: 'neutral' as StatusKind,
    label: String(role || 'user').replace(/_/g, ' '),
  };
  const surface = getStatusSurface(meta.kind, mode);
  return { ...surface, label: meta.label };
}

export function getLifecycleStatusSurface(status: string, mode: PaletteMode): ChipSurface {
  const key = String(status || '').toLowerCase();
  if (['active', 'approved', 'completed', 'compliant', 'enabled'].some((s) => key.includes(s))) {
    return getStatusSurface('success', mode);
  }
  if (['pending', 'in progress', 'in review', 'under review', 'submitted', 'draft'].some((s) => key.includes(s))) {
    return key.includes('draft') || key.includes('pending') || key.includes('submitted')
      ? getStatusSurface('warning', mode)
      : getStatusSurface('info', mode);
  }
  if (['reject', 'denied', 'revoked', 'inactive', 'retired', 'non-compliant', 'expired', 'error'].some((s) => key.includes(s))) {
    return getStatusSurface('error', mode);
  }
  if (['need', 'clarif', 'suspend'].some((s) => key.includes(s))) {
    return getStatusSurface(key.includes('suspend') ? 'info' : 'warning', mode);
  }
  return getStatusSurface('neutral', mode);
}

/** Alternating table row background that works in both modes. */
export function getStripeBg(index: number, mode: PaletteMode): string {
  if (index % 2 === 0) return 'transparent';
  return mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50';
}

/** Sticky/header table cell background. */
export function getTableHeaderBg(mode: PaletteMode): string {
  return mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50';
}

export function chipSxFromSurface(surface: ChipSurface, extra: Record<string, unknown> = {}) {
  return {
    bgcolor: surface.bg,
    color: surface.fg,
    border: `1px solid ${surface.border}`,
    ...extra,
  };
}
